import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { EditorSection, Field } from '~/components/promotion/editor-section';
import { EditorHeader } from '~/components/editor-header';
import { Pill } from '~/components/pill';
import { formatDateTime } from '~/lib/format-date';
import { utf8Bytes, type RobotsFileFormValues } from '~/lib/schemas/robots-file';

interface Props {
	isManaged: boolean;
	lastModifiedUtc?: string | null;
	maxContentBytes: number;
	saving?: boolean;
	onCancel: () => void;
}

/**
 * Storefront robots.txt editor. The content is plain text only — it is never rendered as HTML
 * (a textarea value), and the server serves it as `text/plain`.
 */
export function RobotsFileEditor({ isManaged, lastModifiedUtc, maxContentBytes, saving, onCancel }: Props) {
	const { t } = useTranslation();
	const { control, register, formState } = useFormContext<RobotsFileFormValues>();
	const { isDirty } = useFormState({ control });
	const content = useWatch({ control, name: 'content' });
	const kb = (bytes: number) => (bytes / 1024).toFixed(1);
	const error = formState.errors.content?.message;

	return (
		<div className="flex flex-col gap-4">
			<EditorHeader
				breadcrumb={[{ label: t('menu.system') }, { label: t('system.robotsFile.title') }]}
				title={t('system.robotsFile.title')}
				pills={isManaged ? undefined : <Pill variant="neutral">{t('system.robotsFile.notManagedPill')}</Pill>}
				onCancel={onCancel}
				saving={saving}
				// An unmanaged prefill is savable as-is: saving it is what makes it managed
				isDirty={isDirty || !isManaged}
			/>

			<div className="mx-auto w-full max-w-4xl">
				<EditorSection title={t('system.robotsFile.title')} subtitle={t('system.robotsFile.subtitle')} icon={FileText}>
					<div className="flex flex-col gap-3">
						{!isManaged && (
							<p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground-muted">
								{t('system.robotsFile.notManaged')}
							</p>
						)}
						<Field
							htmlFor="robots-content"
							error={error ? t(error, { defaultValue: error }) : undefined}
							help={t('system.robotsFile.size', { kb: kb(utf8Bytes(content ?? '')), maxKb: Math.floor(maxContentBytes / 1024) })}>
							<Textarea
								id="robots-content"
								{...register('content')}
								className="min-h-[480px] font-mono text-xs"
								spellCheck={false}
								aria-label="robots.txt"
								aria-invalid={Boolean(error)}
							/>
						</Field>
						<p className="text-xs text-foreground-muted">
							{isManaged && lastModifiedUtc ? `${t('system.robotsFile.lastModified', { date: formatDateTime(lastModifiedUtc) })} · ` : ''}
							{t('system.robotsFile.propagationHint')}
						</p>
					</div>
				</EditorSection>
			</div>
		</div>
	);
}
